import React, { useState, useMemo } from 'react';
import { DefaultList as List } from 'services/UIUtils';
import SelectableListItem from 'components/Utils/SelectableListItem';
import DocumentBadge from '../DocumentBadge';
import DefaultButton from 'components/Utils/NewDefaultButton';
import _ from 'lodash';
import DocumentDetailsModal from 'components/HealthRecords/Documents/DocumentDetailsModal';
import Icon from 'components/Utils/NewIcon';
import useFetchPatients from 'hooks/useFetchPatients';
import { useAppSelector } from 'careand-redux/store';

const BUTTON_STYLE = { width: '80%' };

const DocumentList = ({
  documents,
  selected,
  onClick,
  renderBadge,
  getClassName,
  increment,
  highlightUnassigned,
  allowPin,
}) => {
  useFetchPatients(documents);

  const patients = useAppSelector((state) => state.patients.byId);

  const [showNumber, setShowNumber] = useState(increment || 0);
  const [internalSelected, setInternalSelected] = useState();

  const selectedDocument = selected || internalSelected;

  const filteredDocuments = useMemo(() => {
    let documentsCopy = [...(documents || [])];
    if (increment > 0) documentsCopy = documentsCopy.splice(0, showNumber);
    return documentsCopy;
  }, [documents, increment, showNumber]);

  function incrementLimit() {
    setShowNumber(showNumber + increment);
  }

  const selectedIndex = useMemo(() => {
    if (selectedDocument) return _.findIndex(documents, ['id', selectedDocument.id]);
    return -1;
  }, [documents, selectedDocument]);

  function onPrevious() {
    setInternalSelected(documents[selectedIndex - 1]);
  }

  function onNext() {
    setInternalSelected(documents[selectedIndex + 1]);
  }

  const hasNext = selectedIndex !== -1 && selectedIndex < documents.length - 1;
  const hasPrevious = selectedIndex > 0;

  return (
    <div className="document-list">
      <List>
        {filteredDocuments.length === 0 && (
          <span key="no-element" className="padded-title">
            No items to show
          </span>
        )}
        {filteredDocuments.length > 0 &&
          filteredDocuments.map((document) => {
            function clickDocument() {
              if (onClick) onClick(document);
              else setInternalSelected(document);
            }

            return (
              <SelectableListItem
                key={document.id}
                className={'selectable-list-item ' + (getClassName ? getClassName(document) : '')}
                selected={selectedDocument?.id === document.id}
                onClick={clickDocument}
                special={
                  highlightUnassigned &&
                  (!document.patient_id || !document.document_type || !document.physician_id)
                }
                item={
                  <Badge
                    renderBadge={renderBadge}
                    document={document}
                    patient={(patients || {})[document.patient_id]}
                  />
                }
              />
            );
          })}
        {showNumber > 0 && documents.length > showNumber && (
          <div style={{ width: '100%', textAlign: 'center', marginTop: '15px' }}>
            <DefaultButton name="Load more" style={BUTTON_STYLE} onClick={incrementLimit} />
          </div>
        )}
      </List>
      {!onClick && !!selectedDocument && (
        <DocumentDetailsModal
          key={selectedDocument?.id}
          allowPin={allowPin}
          isOpen={!!selectedDocument}
          document={selectedDocument}
          onClose={() => setInternalSelected()}
          headerContent={
            <div
              style={{ width: '300px', display: 'flex', justifyContent: 'center', margin: 'auto' }}
            >
              {hasPrevious && (
                <div onClick={onPrevious} className="clickable">
                  <Icon src="arrow-left" />
                </div>
              )}
              <span className="mx-2">Edit Document</span>
              {hasNext && (
                <div onClick={onNext} className="clickable">
                  <Icon src="arrow-right" />
                </div>
              )}
            </div>
          }
        />
      )}
    </div>
  );
};

const Badge = React.memo(({ document, renderBadge, patient }) => (
  <React.Fragment>
    {renderBadge !== undefined ? renderBadge(document) : null}
    {renderBadge === undefined && <DocumentBadge item={document} patient={patient} />}
  </React.Fragment>
));

export default DocumentList;
